import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';

/**
 * Register component for user registration
 * Allows new users to create an account
 */
const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [country, setCountry] = useState('Ireland');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('Default');
  const [patterns, setPatterns] = useState({});
  const [selectedPattern, setSelectedPattern] = useState('Standard 9-5');
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState('Day');
  const navigate = useNavigate();

  // Fetch companies and shift patterns when component mounts
  useEffect(() => {
    const fetchCompaniesAndPatterns = async () => {
      try {
        // Fetch companies
        const companiesRef = collection(db, 'companies');
        const companiesSnapshot = await getDocs(companiesRef);
        const companiesList = companiesSnapshot.docs.map(doc => doc.data().name);
        
        // Add Default company if not already in list
        if (!companiesList.includes('Default')) {
          companiesList.push('Default');
        }
        
        setCompanies(companiesList);
        
        // Fetch shift patterns
        const shiftsRef = collection(db, 'shiftPatterns');
        const querySnapshot = await getDocs(shiftsRef);
        
        const companyPatterns = {};
        const shiftOptions = new Set();
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const companyName = data.company;
          const patternName = data.patternName;
          const shiftName = data.shift;
          
          if (!companyPatterns[companyName]) {
            companyPatterns[companyName] = [];
          }
          
          if (!companyPatterns[companyName].includes(patternName)) {
            companyPatterns[companyName].push(patternName);
          }
          
          shiftOptions.add(shiftName);
        });
        
        setPatterns(companyPatterns);
        setShifts(Array.from(shiftOptions));
        
        // Set default pattern if available
        if (companyPatterns['Default'] && companyPatterns['Default'].includes('Standard 9-5')) {
          setSelectedPattern('Standard 9-5');
        } else if (companiesList.length > 0 && companyPatterns[companiesList[0]]) {
          setSelectedPattern(companyPatterns[companiesList[0]][0]);
        }
      } catch (error) {
        console.error('Error fetching companies and patterns:', error);
      }
    };

    fetchCompaniesAndPatterns();
  }, []);

  // Handle company change
  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    
    // Reset pattern and shift when company changes
    if (patterns[company] && patterns[company].length > 0) {
      setSelectedPattern(patterns[company][0]);
    } else {
      setSelectedPattern('');
    }
  };

  // Handle pattern change
  const handlePatternChange = (e) => {
    setSelectedPattern(e.target.value);
  };

  // Handle shift change
  const handleShiftChange = (e) => {
    setSelectedShift(e.target.value);
  };

  // Handle country change
  const handleCountryChange = (e) => {
    setCountry(e.target.value);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form inputs
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore with shift preferences
      await setDoc(doc(db, 'users', user.uid), {
        username: username,
        email: email,
        country: country,
        shiftPreferences: {
          company: selectedCompany,
          pattern: selectedPattern,
          shift: selectedShift
        },
        createdAt: new Date()
      });
      
      // Create empty friends collection for the user
      await setDoc(doc(db, 'users', user.uid, 'friends', 'placeholder'), {
        placeholder: true
      });
      
      navigate('/');
    } catch (error) {
      let errorMessage = 'Failed to create an account.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email is already in use.';
      }
      
      setError(errorMessage);
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center">
      <Card className="w-100" style={{ maxWidth: '400px' }}>
        <Card.Header>
          <h2 className="text-center mb-0">Register</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formConfirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Form.Group>

            {/* Shift Preference Section */}
            <h5 className="mb-3 mt-4">Shift Preferences</h5>
            
            <Form.Group className="mb-3" controlId="formCountry">
              <Form.Label>Country (for Bank Holidays)</Form.Label>
              <Form.Select
                value={country}
                onChange={handleCountryChange}
                required
              >
                <option value="Ireland">Ireland</option>
                <option value="UK">UK</option>
              </Form.Select>
            </Form.Group>
            
            <Row>
              <Col>
                <Form.Group className="mb-3" controlId="formCompany">
                  <Form.Label>Company</Form.Label>
                  <Form.Select
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    required
                  >
                    {companies.map((company, idx) => (
                      <option key={idx} value={company}>
                        {company}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col>
                <Form.Group className="mb-3" controlId="formPattern">
                  <Form.Label>Pattern</Form.Label>
                  <Form.Select
                    value={selectedPattern}
                    onChange={handlePatternChange}
                    required
                    disabled={!patterns[selectedCompany] || patterns[selectedCompany].length === 0}
                  >
                    {patterns[selectedCompany]?.map((pattern, idx) => (
                      <option key={idx} value={pattern}>
                        {pattern}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col>
                <Form.Group className="mb-3" controlId="formShift">
                  <Form.Label>Shift</Form.Label>
                  <Form.Select
                    value={selectedShift}
                    onChange={handleShiftChange}
                    required
                  >
                    {shifts.map((shift, idx) => (
                      <option key={idx} value={shift}>
                        {shift}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Button 
              variant="primary" 
              type="submit" 
              className="w-100 mt-4" 
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register'}
            </Button>
          </Form>
        </Card.Body>
        <Card.Footer className="text-center">
          Already have an account? <Link to="/login">Login</Link>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Register;
